import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert
} from '@mui/material';
import { 
  Schedule, 
  CheckCircle, 
  ArrowBack,
  AccessTime
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import logger from '../../../utils/logger';
const HeaderContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)',
  color: 'white',
  padding: '40px 30px',
  textAlign: 'center',
  position: 'relative'
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 20px',
  backdropFilter: 'blur(10px)'
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  padding: '30px',
  maxHeight: '500px',
  overflowY: 'auto'
}));

const DayCard = styled(Card)(({ theme }) => ({
  marginBottom: '12px',
  border: '1px solid #e0e0e0'
}));

const ActionButton = styled(Button)(({ theme, variant: buttonVariant }) => ({
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  ...(buttonVariant === 'primary' && {
    backgroundColor: '#2196f3',
    color: 'white',
    '&:hover': {
      backgroundColor: '#1976d2'
    }
  }),
  ...(buttonVariant === 'secondary' && {
    backgroundColor: '#f5f5f5',
    color: '#333',
    '&:hover': {
      backgroundColor: '#e0e0e0'
    }
  })
}));

const BusinessHoursPopup = ({ task, onComplete, onBack, businessProfileData = {}, profileId }) => {
  const [businessHours, setBusinessHours] = useState({});
  const [step, setStep] = useState('edit'); // edit, success

  const storageKey = profileId ? `visibeen_hours_${profileId}` : 'business_hours';

  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  const timeSlots = [
    '12:00 AM', '12:30 AM', '1:00 AM', '1:30 AM', '2:00 AM', '2:30 AM',
    '3:00 AM', '3:30 AM', '4:00 AM', '4:30 AM', '5:00 AM', '5:30 AM',
    '6:00 AM', '6:30 AM', '7:00 AM', '7:30 AM', '8:00 AM', '8:30 AM',
    '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
    '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM',
    '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM',
    '9:00 PM', '9:30 PM', '10:00 PM', '10:30 PM', '11:00 PM', '11:30 PM'
  ];

  const convertGMBHours = (regularHours) => {
    if (!regularHours?.periods) return null;
    const hoursMap = {};
    regularHours.periods.forEach(period => {
      const openDay = period.openDay?.toLowerCase();
      if (!openDay) return;
      const dayKey = openDay;
      const openTime = period.openTime || '09:00';
      const closeTime = period.closeTime || '17:00';

      const formatTime = (timeInput) => {
        if (!timeInput) return '9:00 AM';
        
        let hours = 9, minutes = 0;
        
        // Handle object format {hours: 9, minutes: 0} from Google API
        if (typeof timeInput === 'object') {
          hours = timeInput.hours || 0;
          minutes = timeInput.minutes || 0;
        } 
        // Handle string format "0900" or "09:00"
        else if (typeof timeInput === 'string') {
          const cleanTime = timeInput.replace(':', '');
          if (cleanTime.length >= 4) {
            hours = parseInt(cleanTime.slice(0, 2), 10);
            minutes = parseInt(cleanTime.slice(2, 4), 10);
          }
        }
        
        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes);
        return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      };

      hoursMap[dayKey] = {
        isOpen: true,
        openTime: formatTime(openTime),
        closeTime: formatTime(closeTime)
      };
    });

    return hoursMap;
  };

  useEffect(() => {
    // Load existing hours or set defaults
    try {
      const savedHours = JSON.parse(localStorage.getItem(storageKey) || '{}');
      if (savedHours && Object.keys(savedHours).length > 0) {
        setBusinessHours(savedHours);
        return;
      }
    } catch (error) {
      logger.warn('Failed to parse stored business hours:', error);
    }

    const gmbHours = convertGMBHours(businessProfileData.regularHours);

    const defaultHours = {
      monday: { isOpen: true, openTime: '9:00 AM', closeTime: '5:00 PM' },
      tuesday: { isOpen: true, openTime: '9:00 AM', closeTime: '5:00 PM' },
      wednesday: { isOpen: true, openTime: '9:00 AM', closeTime: '5:00 PM' },
      thursday: { isOpen: true, openTime: '9:00 AM', closeTime: '5:00 PM' },
      friday: { isOpen: true, openTime: '9:00 AM', closeTime: '5:00 PM' },
      saturday: { isOpen: true, openTime: '10:00 AM', closeTime: '4:00 PM' },
      sunday: { isOpen: false, openTime: '10:00 AM', closeTime: '4:00 PM' }
    };

    setBusinessHours(gmbHours && Object.keys(gmbHours).length ? gmbHours : defaultHours);
  }, [businessProfileData, storageKey]);

  const handleDayToggle = (day) => {
    setBusinessHours({
      ...businessHours,
      [day]: {
        ...businessHours[day],
        isOpen: !businessHours[day]?.isOpen
      }
    });
  };

  const handleTimeChange = (day, timeType, value) => {
    setBusinessHours({
      ...businessHours,
      [day]: {
        ...businessHours[day],
        [timeType]: value
      }
    });
  };

  const handleSaveHours = () => {
    // Save to localStorage
    localStorage.setItem(storageKey, JSON.stringify(businessHours));
    setStep('success');
  };

  const handleCompleteTask = () => {
    const openDays = Object.values(businessHours).filter(day => day.isOpen).length;
    const totalDays = Object.keys(businessHours).length;
    const weekendCoverage = ['saturday', 'sunday'].filter(day => businessHours[day]?.isOpen).length;
    
    onComplete({
      type: 'hours',
      completed: true,
      hours: businessHours,
      openDays,
      totalDays,
      completionRate: Math.round((openDays / totalDays) * 100),
      businessHours,
      weekendCoverage,
      completedAt: new Date().toISOString()
    });
  };

  const renderHoursEditor = () => (
    <ContentContainer>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#333' }}>
        Set Your Business Hours
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Accurate business hours help customers know when you're available and improve your local search visibility.
        </Typography>
      </Alert>

      <Grid container spacing={2}>
        {daysOfWeek.map((day) => (
          <Grid item xs={12} key={day.key}>
            <DayCard>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500, minWidth: '100px' }}>
                      {day.label}
                    </Typography>
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={businessHours[day.key]?.isOpen || false}
                          onChange={() => handleDayToggle(day.key)}
                          color="primary"
                        />
                      }
                      label={businessHours[day.key]?.isOpen ? 'Open' : 'Closed'}
                      sx={{ ml: 2, mr: 3 }}
                    />
                  </Box>

                  {businessHours[day.key]?.isOpen && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={businessHours[day.key]?.openTime || '9:00 AM'}
                          onChange={(e) => handleTimeChange(day.key, 'openTime', e.target.value)}
                        >
                          {timeSlots.map((time) => (
                            <MenuItem key={time} value={time}>
                              {time}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      <Typography variant="body2" color="textSecondary">
                        to
                      </Typography>
                      
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={businessHours[day.key]?.closeTime || '5:00 PM'}
                          onChange={(e) => handleTimeChange(day.key, 'closeTime', e.target.value)}
                        >
                          {timeSlots.map((time) => (
                            <MenuItem key={time} value={time}>
                              {time}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </DayCard>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Box sx={{ mt: 3, mb: 4 }}>
        <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
          Quick Actions:
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const weekdayHours = { isOpen: true, openTime: '9:00 AM', closeTime: '5:00 PM' };
              setBusinessHours({
                ...businessHours,
                monday: weekdayHours,
                tuesday: weekdayHours,
                wednesday: weekdayHours,
                thursday: weekdayHours,
                friday: weekdayHours
              });
            }}
          >
            Set Weekday Hours (9-5)
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const weekendHours = { isOpen: true, openTime: '10:00 AM', closeTime: '4:00 PM' };
              setBusinessHours({
                ...businessHours,
                saturday: weekendHours,
                sunday: weekendHours
              });
            }}
          >
            Set Weekend Hours (10-4)
          </Button>
        </Stack>
      </Box>

      <Stack direction="row" spacing={2} sx={{ justifyContent: 'center' }}>
        <ActionButton variant="secondary" onClick={onBack} startIcon={<ArrowBack />}>
          Back
        </ActionButton>
        <ActionButton 
          variant="primary" 
          onClick={handleSaveHours}
          startIcon={<Schedule />}
        >
          Save Business Hours
        </ActionButton>
      </Stack>
    </ContentContainer>
  );

  const renderSuccess = () => {
    const openDays = Object.values(businessHours).filter(day => day.isOpen).length;
    const totalDays = Object.keys(businessHours).length;

    return (
      <ContentContainer>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircle sx={{ fontSize: 80, color: '#4caf50', mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
            Business Hours Updated!
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
            Your business hours have been saved and will help customers know when you're available.
          </Typography>

          <Card sx={{ mb: 4, textAlign: 'left' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Hours Summary
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="textSecondary">Open Days:</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#2196f3' }}>
                    {openDays} / {totalDays}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>Weekly Schedule:</Typography>
                  {daysOfWeek.map((day) => (
                    <Box key={day.key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {day.label}:
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {businessHours[day.key]?.isOpen 
                          ? `${businessHours[day.key].openTime} - ${businessHours[day.key].closeTime}`
                          : 'Closed'
                        }
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <ActionButton 
            variant="primary" 
            startIcon={<CheckCircle />}
            onClick={handleCompleteTask}
          >
            Complete Task
          </ActionButton>
        </Box>
      </ContentContainer>
    );
  };

  return (
    <Box>
      <HeaderContainer>
        <IconContainer>
          <Schedule sx={{ fontSize: 40, color: 'white' }} />
        </IconContainer>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Business Hours
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Set your business hours to help customers know when you're available
        </Typography>
      </HeaderContainer>

      {step === 'edit' && renderHoursEditor()}
      {step === 'success' && renderSuccess()}
    </Box>
  );
};

export default BusinessHoursPopup;
